"use client";

import { createAccountLocalisedSchema, InviteEmployeeSchema } from "@/lib/validation/account";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/atoms/buttons";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { useCustomer } from "@/hooks/useCustomer";
import { toast, Toast } from "@/components/atoms/toast";
import { useAllEmployees, useCompanyDetails } from "@/queries/company";
import { useTransition } from "react";
import { sdk } from "@/lib/medusa/client";
import { Loader2 } from "lucide-react";

interface AccountInviteEmployeeFormProps {
  locale: Locales;
}

const AccountInviteEmployeeForm = ({ locale }: AccountInviteEmployeeFormProps) => {
  const { inviteEmployeeSchema } = createAccountLocalisedSchema(locale);
  const { customer } = useCustomer();
  const [isPending, startTransition] = useTransition();
  const form = useForm<InviteEmployeeSchema>({
    resolver: zodResolver(inviteEmployeeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const companyName = customer?.company_name;

  const { data: companyDetails, isLoading: isCompanyLoading } = useCompanyDetails(
    companyName || "",
    customer?.id || "anonymous"
  );
  const { data: employees, isLoading: isEmployeesLoading } = useAllEmployees(companyDetails?.id || "");

  const isLoading = isCompanyLoading || isEmployeesLoading;

  const { handleSubmit, control, reset } = form;

  const currentEmployee = employees
    ?.filter((employee) => employee.customer)
    .find((filteredEmployee) => filteredEmployee.customer.id === customer?.id);

  const onSubmit = (data: InviteEmployeeSchema) => {
    if (!companyDetails || !companyDetails.id) return;
    startTransition(async () => {
      try {
        const signupRedirectUrl = window.location.origin + getPermalink(locale, "signup");
        const loginRedirectUrl = window.location.origin + getPermalink(locale, "login");
        await sdk.client.fetch(`store/companies/${companyDetails.id}/invite`, {
          method: "POST",
          body: {
            email: data.email,
            isAdmin: false,
            companyId: companyDetails.id,
            invitedBy: customer?.id,
            url: signupRedirectUrl,
            loginurl: loginRedirectUrl,
            lang: locale,
          },
        });
        toast.success({
          title: __("Invitation sent successfully!", locale),
          description: __("The invitation link has been sent to the employee's email address.", locale),
        });
      } catch (error) {
        if (error instanceof SyntaxError && error.message.includes("Unexpected end of JSON input")) {
          toast.success({
            title: __("Invitation sent successfully!", locale),
            description: __("The invitation link has been sent to the employee's email address.", locale),
          });
          return;
        }
        console.error("Error inviting employee:", error);
        toast.error({ title: __("An error occurred while submitting the form. Please try again later.", locale) });
        return;
      } finally {
        reset();
      }
    });
  };

  if (isLoading)
    return (
      <section className="border-primary-light/50 space-y-5 border-t pt-5">
        <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Invite an employee", locale)}</h2>
        <div className="flex items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      </section>
    );

  if (currentEmployee && !currentEmployee.is_admin) return null;

  return (
    <section className="border-primary-light/50 space-y-5 border-t pt-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Invite an employee", locale)}</h2>
      {!companyName ? (
        <Toast
          type="info"
          id="message"
          title={__("You need to create a company first to invite employees.", locale)}
          className="max-w-none shadow-none"
        />
      ) : (
        <>
          <Toast
            id="account-invite-employee-form"
            type="info"
            title={__("An invitation to join your company account has been sent to the contacts below.", locale)}
            className="max-w-none shadow-none"
          />
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid gap-x-2.5 gap-y-4 sm:grid-cols-3 lg:gap-x-5">
                <FormField
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="w-full gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">{__("First name", locale)}</FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            type="text"
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="w-full gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">{__("Last name", locale)}</FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            type="text"
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">{__("Email", locale)}</FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            type="email"
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  size="md"
                  disabled={isPending}
                  variant="rounded"
                  className="w-full text-base font-semibold sm:w-fit"
                  type="submit"
                >
                  {isPending && <Loader2 className="mr-2 animate-spin" />} {__("Send the invitation", locale)}
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </section>
  );
};

export default AccountInviteEmployeeForm;
